import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Trophy, Medal, FileDown, Monitor, Lock, LockOpen, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSiteLogo } from "@/lib/siteConfig";

const discLabel = { artistica: "Ginnastica Artistica", ritmica: "Ginnastica Ritmica" };
const rankColor = { 1: "text-[#D4AF37]", 2: "text-[#C0C0C0]", 3: "text-[#CD7F32]" };
const PODIUM_RGB = { 1: [212, 175, 55], 2: [210, 210, 210], 3: [205, 127, 50] };

async function urlToImage(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new window.Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  const fmt = dataUrl.substring(dataUrl.indexOf("/") + 1, dataUrl.indexOf(";")).toUpperCase();
  return { dataUrl, width: img.naturalWidth, height: img.naturalHeight, fmt: fmt === "JPG" ? "JPEG" : fmt };
}

export default function RankingsSection() {
  const { user } = useAuth();
  const isManager = ["admin", "segreteria", "direttore"].includes(user?.role);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("artistica");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("all");
  const [scope, setScope] = useState("apparatus");
  const [locking, setLocking] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const logoUrl = useSiteLogo();

  const loadEvents = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => {
    loadEvents();
  }, []);

  const toggleLock = async (locked) => {
    if (!selectedEvent) return;
    setLocking(true);
    try {
      await api.put(`/events/${selectedEvent.id}/lock`, { locked });
      if (locked) {
        // Genera e salva automaticamente il verbale ufficiale (PDF della classifica)
        try {
          const blob = await generateVerbaleBlob(selectedEvent);
          const slug = selectedEvent.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30);
          const fd = new FormData();
          fd.append("file", blob, `verbale-${slug}.pdf`);
          await api.post(`/events/${selectedEvent.id}/snapshot`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("Gara chiusa: classifica congelata e verbale ufficiale salvato");
        } catch (e) {
          toast.warning("Gara chiusa, ma non è stato possibile salvare il verbale PDF. Riprova dal pulsante Riapri/Chiudi.");
        }
      } else {
        toast.success("Gara riaperta: i voti sono di nuovo modificabili");
      }
      await loadEvents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita");
    } finally {
      setLocking(false);
    }
  };

  // Renderizza una sezione (elenco di raggruppamenti classifica) nel PDF
  const renderRankingSection = (doc, title, gs, y) => {
    if (!gs || gs.length === 0) return y;
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFontSize(13); doc.setTextColor(212, 175, 55);
    doc.text(title, 14, y); y += 6;
    gs.forEach((g) => {
      const parts = [discLabel[g.discipline] || g.discipline, `Categoria: ${g.category}`];
      if (g.group_type && g.group_type !== "—") parts.push(`Tipo: ${g.group_type}`);
      if (g.exercise_type && g.exercise_type !== "—") parts.push(`Esercizio: ${g.exercise_type}`);
      if (g.apparatus && g.apparatus !== "—") parts.push(`Attrezzo: ${g.apparatus}`);
      if (y > 258) { doc.addPage(); y = 20; }
      doc.setFontSize(10); doc.setTextColor(20, 20, 20);
      doc.text(parts.join("  •  "), 14, y);
      autoTable(doc, {
        startY: y + 3,
        head: [["Pos.", "Atleta", "Società", "Giudizi", "Punteggio"]],
        body: g.athletes.map((a) => [a.rank, a.name, a.club || "—", a.num_scores, a.total.toFixed(3)]),
        theme: "grid",
        headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold" },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: { 0: { halign: "center", cellWidth: 14 }, 3: { halign: "center" }, 4: { halign: "right", fontStyle: "bold" } },
        margin: { left: 14, right: 14 },
        didParseCell: (data) => {
          if (data.section === "body") {
            const c = PODIUM_RGB[data.row.raw[0]];
            if (c) { data.cell.styles.fillColor = c; data.cell.styles.textColor = [20, 20, 20]; data.cell.styles.fontStyle = "bold"; }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    });
    return y + 2;
  };

  // Genera il verbale ufficiale completo (per attrezzo, generale e gruppi) come Blob PDF
  const generateVerbaleBlob = async (ev) => {
    const [appRes, genRes, grpRes] = await Promise.all([
      api.get("/rankings", { params: { scope: "apparatus", event_id: ev.id } }),
      api.get("/rankings", { params: { scope: "general", event_id: ev.id } }),
      api.get("/rankings/groups", { params: { event_id: ev.id } }),
    ]);
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const now = new Date().toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    let textX = 14;
    if (logoUrl) {
      try {
        const logo = await urlToImage(logoUrl);
        const h = 18, w = Math.min(40, (logo.width / logo.height) * h);
        doc.addImage(logo.dataUrl, logo.fmt, 14, 10, w, h);
        textX = 14 + w + 5;
      } catch (e) { /* logo opzionale */ }
    }
    doc.setFontSize(18); doc.setTextColor(20, 20, 20);
    doc.text("OPES Ginnastiche", textX, 17);
    let ly = 24;
    doc.setFontSize(13); doc.setTextColor(30, 30, 30);
    doc.text(ev.title, textX, ly); ly += 6;
    doc.setFontSize(10); doc.setTextColor(90, 90, 90);
    const meta = ["Verbale ufficiale — Classifica"];
    if (ev.location) meta.push(ev.location);
    if (ev.date) meta.push(new Date(ev.date).toLocaleDateString("it-IT"));
    doc.text(meta.join("  •  "), textX, ly); ly += 5;
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Documento generato il ${now}`, textX, ly);
    const headerBottom = Math.max(34, ly + 3);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, headerBottom, 196, headerBottom);
    let y = headerBottom + 8;
    y = renderRankingSection(doc, "Classifiche per attrezzo", appRes.data, y);
    y = renderRankingSection(doc, "Classifica generale", genRes.data, y);
    y = renderRankingSection(doc, "Classifiche a squadre / gruppi", grpRes.data, y);
    return doc.output("blob");
  };

  // Sezione Programma di lavoro per il PDF di fine gara
  const renderProgramSection = (doc, athletes, groups, y) => {
    const hasA = athletes && athletes.length;
    const hasG = groups && groups.length;
    if (!hasA && !hasG) return y;
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFontSize(13); doc.setTextColor(212, 175, 55);
    doc.text("Programma di lavoro (ordine di esibizione)", 14, y); y += 6;
    const hs = { fillColor: [15, 23, 42], textColor: [212, 175, 55], fontStyle: "bold" };
    const st = { fontSize: 8, cellPadding: 1.8 };
    if (hasA) {
      const sorted = [...athletes].sort((a, b) => (a.performance_order || 0) - (b.performance_order || 0));
      autoTable(doc, {
        startY: y + 1,
        head: [["#", "Atleta", "Società", "Disciplina", "Categoria", "Musica"]],
        body: sorted.map((a, i) => [a.performance_order || i + 1, `${a.first_name} ${a.last_name}`, a.club || "—", discLabel[a.discipline] || a.discipline, a.category || "—", a.music_title || (a.music_url ? "Link esterno" : "—")]),
        theme: "grid", headStyles: hs, styles: st, margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
    if (hasG) {
      if (y > 250) { doc.addPage(); y = 20; }
      autoTable(doc, {
        startY: y + 1,
        head: [["Squadra", "Tipo", "Categoria", "Musica", "N. componenti"]],
        body: groups.map((g) => [g.name, g.group_type || "—", g.category || "—", g.music_title || (g.music_url ? "Link esterno" : "—"), String((g.member_ids || []).length)]),
        theme: "grid", headStyles: hs, styles: st, margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
    return y + 2;
  };

  // Sezione Registro voti (audit) per il PDF di fine gara
  const renderAuditSection = (doc, entries, y) => {
    if (!entries || !entries.length) return y;
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFontSize(13); doc.setTextColor(212, 175, 55);
    doc.text("Registro voti (storico modifiche)", 14, y); y += 6;
    const labels = { create: "Inserimento", update: "Modifica", confirm: "Conferma", delete: "Eliminazione" };
    autoTable(doc, {
      startY: y + 1,
      head: [["Data/ora", "Azione", "Atleta", "Attrezzo", "Giudice", "Operatore", "Variazione"]],
      body: entries.map((e) => [
        e.at ? new Date(e.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—",
        labels[e.action] || e.action || "—",
        e.athlete_name || "—", e.apparatus || "—", e.judge_name || "—", e.actor_name || "—",
        (e.old_total != null || e.new_total != null) ? `${e.old_total != null ? e.old_total : ""}${(e.old_total != null && e.new_total != null) ? " → " : ""}${e.new_total != null ? e.new_total : ""}` : "—",
      ]),
      theme: "grid",
      headStyles: { fillColor: [15, 23, 42], textColor: [212, 175, 55], fontStyle: "bold" },
      styles: { fontSize: 7.5, cellPadding: 1.5 }, margin: { left: 14, right: 14 },
    });
    return doc.lastAutoTable.finalY + 6;
  };

  // Genera il PDF unico di FINE GARA (classifiche + programma + registro voti) per l'archivio
  const generateFinalArchiveBlob = async (ev) => {
    const [appRes, genRes, grpRes, athRes, grp2Res, auditRes] = await Promise.all([
      api.get("/rankings", { params: { scope: "apparatus", event_id: ev.id } }),
      api.get("/rankings", { params: { scope: "general", event_id: ev.id } }),
      api.get("/rankings/groups", { params: { event_id: ev.id } }),
      api.get("/athletes", { params: { event_id: ev.id } }).catch(() => ({ data: [] })),
      api.get("/groups", { params: { event_id: ev.id } }).catch(() => ({ data: [] })),
      api.get("/scores/audit").catch(() => ({ data: [] })),
    ]);
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const now = new Date().toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    let textX = 14;
    if (logoUrl) {
      try {
        const logo = await urlToImage(logoUrl);
        const h = 18, w = Math.min(40, (logo.width / logo.height) * h);
        doc.addImage(logo.dataUrl, logo.fmt, 14, 10, w, h);
        textX = 14 + w + 5;
      } catch (e) { /* logo opzionale */ }
    }
    doc.setFontSize(18); doc.setTextColor(20, 20, 20);
    doc.text("OPES Ginnastiche", textX, 17);
    let ly = 24;
    doc.setFontSize(13); doc.setTextColor(30, 30, 30);
    doc.text(ev.title, textX, ly); ly += 6;
    doc.setFontSize(10); doc.setTextColor(90, 90, 90);
    const meta = ["Documento di archivio di fine gara"];
    if (ev.location) meta.push(ev.location);
    if (ev.date) meta.push(new Date(ev.date).toLocaleDateString("it-IT"));
    doc.text(meta.join("  •  "), textX, ly); ly += 5;
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Generato il ${now}`, textX, ly);
    const headerBottom = Math.max(34, ly + 3);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, headerBottom, 196, headerBottom);
    let y = headerBottom + 8;
    y = renderRankingSection(doc, "Classifiche per attrezzo", appRes.data, y);
    y = renderRankingSection(doc, "Classifica generale", genRes.data, y);
    y = renderRankingSection(doc, "Classifiche a squadre / gruppi", grpRes.data, y);
    y = renderProgramSection(doc, athRes.data, grp2Res.data, y);
    const ids = new Set([...(athRes.data || []).map((a) => a.id), ...(grp2Res.data || []).map((g) => g.id)]);
    const auditEv = (auditRes.data || []).filter((e) => ids.has(e.athlete_id));
    y = renderAuditSection(doc, auditEv, y);
    return doc.output("blob");
  };

  const exportFinalArchive = async () => {
    if (!selectedEvent) return;
    setArchiving(true);
    try {
      const blob = await generateFinalArchiveBlob(selectedEvent);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fine-gara-${selectedEvent.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF di fine gara generato");
    } catch (e) {
      toast.error("Impossibile generare il PDF di fine gara");
    } finally {
      setArchiving(false);
    }
  };

  useEffect(() => {
    let active = true;
    const isGroups = scope === "groups";
    const params = isGroups ? {} : { scope };
    if (eventId !== "all") params.event_id = eventId;
    const fetchRankings = (showLoader) => {
      if (showLoader) setLoading(true);
      api.get(isGroups ? "/rankings/groups" : "/rankings", { params }).then((r) => { if (active) setGroups(r.data); }).finally(() => { if (active && showLoader) setLoading(false); });
    };
    fetchRankings(true);
    const t = setInterval(() => fetchRankings(false), 8000); // Classifica Live: aggiornamento istantaneo
    return () => { active = false; clearInterval(t); };
  }, [eventId, scope]);

  const selectedEvent = events.find((e) => e.id === eventId) || null;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const byDisc = (d) => groups.filter((g) => g.discipline === d);

  const exportPDF = async (d) => {
    const gs = byDisc(d);
    if (gs.length === 0) { toast.error("Nessuna classifica da esportare"); return; }
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const now = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    let textX = 14, headerBottom = 34;
    if (logoUrl) {
      try {
        const logo = await urlToImage(logoUrl);
        const h = 18, w = Math.min(40, (logo.width / logo.height) * h);
        doc.addImage(logo.dataUrl, logo.fmt, 14, 10, w, h);
        textX = 14 + w + 5;
      } catch (e) { /* logo opzionale */ }
    }
    doc.setFontSize(18); doc.setTextColor(20, 20, 20);
    doc.text("OPES Ginnastiche", textX, 17);
    let ly = 24;
    if (selectedEvent) {
      doc.setFontSize(13); doc.setTextColor(30, 30, 30);
      doc.text(selectedEvent.title, textX, ly); ly += 6;
      doc.setFontSize(10); doc.setTextColor(90, 90, 90);
      const meta = [scope === "general" ? "Classifica Generale" : `Classifiche — ${discLabel[d]}`];
      if (selectedEvent.location) meta.push(selectedEvent.location);
      if (selectedEvent.date) meta.push(new Date(selectedEvent.date).toLocaleDateString("it-IT"));
      doc.text(meta.join("  •  "), textX, ly); ly += 5;
    } else {
      doc.setFontSize(13); doc.setTextColor(90, 90, 90);
      doc.text(scope === "general" ? `Classifica Generale — ${discLabel[d]}` : `Classifiche — ${discLabel[d]}`, textX, ly); ly += 6;
    }
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`Generato il ${now}`, textX, ly);
    headerBottom = Math.max(34, ly + 3);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, headerBottom, 196, headerBottom);
    let y = headerBottom + 8;
    gs.forEach((g) => {
      const parts = [`Categoria: ${g.category}`];
      if (g.exercise_type && g.exercise_type !== "—") parts.push(`Tipo: ${g.exercise_type}`);
      if (g.apparatus && g.apparatus !== "—") parts.push(`Attrezzo: ${g.apparatus}`);
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(11); doc.setTextColor(20, 20, 20);
      doc.text(parts.join("  •  "), 14, y);
      autoTable(doc, {
        startY: y + 3,
        head: [["Pos.", "Atleta", "Società", "Giudizi", "Punteggio"]],
        body: g.athletes.map((a) => [a.rank, a.name, a.club || "—", a.num_scores, a.total.toFixed(3)]),
        theme: "grid",
        headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold" },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: { 0: { halign: "center", cellWidth: 14 }, 3: { halign: "center" }, 4: { halign: "right", fontStyle: "bold" } },
        margin: { left: 14, right: 14 },
        didParseCell: (data) => {
          if (data.section === "body") {
            const c = PODIUM_RGB[data.row.raw[0]];
            if (c) { data.cell.styles.fillColor = c; data.cell.styles.textColor = [20, 20, 20]; data.cell.styles.fontStyle = "bold"; }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 10;
    });
    const slug = selectedEvent ? `-${selectedEvent.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}` : "";
    doc.save(`risultati-${d}${slug}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF generato");
  };

  const renderGroups = (d) => {
    const gs = byDisc(d);
    if (gs.length === 0) return <div className="text-center py-16 text-muted-foreground border border-dashed border-white/10 rounded-xl">Nessuna classifica disponibile.</div>;
    return (
      <div className="space-y-8">
        {gs.map((g) => (
          <div key={`${g.discipline}-${g.group_type || ''}-${g.category}-${g.exercise_type}-${g.apparatus}`} data-testid={`ranking-group-${g.discipline}-${g.category}-${g.exercise_type}-${g.apparatus}`}>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Trophy className="w-4 h-4 text-gold" />
              <h3 className="text-lg font-semibold">Categoria: <span className="text-gold">{g.category}</span></h3>
              {g.group_type && <span className="text-sm px-2.5 py-1 rounded-full border border-gold/30 bg-gold/10 text-gold capitalize">{g.group_type}</span>}
              {g.exercise_type && g.exercise_type !== "—" && <span className="text-sm px-2.5 py-1 rounded-full border border-white/15 text-muted-foreground">Tipo: <span className="text-gold">{g.exercise_type}</span></span>}
              {g.apparatus && g.apparatus !== "—" && <span className="text-sm px-2.5 py-1 rounded-full border border-white/15 text-muted-foreground capitalize">Attrezzo: <span className="text-gold">{g.apparatus}</span></span>}
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0F172A] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="w-16">Pos.</TableHead>
                    <TableHead>Atleta</TableHead>
                    <TableHead>Società</TableHead>
                    <TableHead className="text-right">Giudizi</TableHead>
                    <TableHead className="text-right">Punteggio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.athletes.map((a, i) => (
                    <motion.tr key={a.athlete_id} data-testid={`ranking-row-${a.athlete_id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="border-b border-white/10">
                      <TableCell>
                        {a.rank <= 3 ? (
                          <span className={`font-display text-2xl ${rankColor[a.rank]} flex items-center gap-1`}>
                            <Medal className={`w-4 h-4 ${rankColor[a.rank]}`} />{a.rank}
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 bg-white/5 font-display text-lg text-foreground">
                            {a.rank}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell className="text-muted-foreground">{a.club || "—"}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{a.num_scores}</TableCell>
                      <TableCell className="text-right font-display text-2xl text-gold">{a.total.toFixed(3)}</TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <p className="text-muted-foreground">Classifica Live: aggiornata automaticamente quando tutti i giudici confermano i voti.</p>
        <span className="inline-flex items-center gap-1.5 text-xs text-[#10B981]"><span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> Live</span>
      </div>
      {selectedEvent?.locked && (
        <div data-testid="ranking-locked-banner" className="flex items-start gap-3 rounded-xl border border-[#10B981]/40 bg-[#10B981]/10 p-4 mb-4">
          <Lock className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#10B981]">Classifica ufficiale — gara chiusa</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Questa gara è stata chiusa{selectedEvent.locked_by ? ` da ${selectedEvent.locked_by}` : ""}{selectedEvent.locked_at ? ` il ${new Date(selectedEvent.locked_at).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}. I voti sono congelati e non modificabili{isManager ? " finché non riapri la gara" : ""}.
              {selectedEvent.snapshot?.url ? " È disponibile il verbale ufficiale in PDF." : ""}
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-4">
        <div className="inline-flex rounded-lg border border-white/10 bg-[#0F172A] p-1">
          <button data-testid="ranking-scope-apparatus" onClick={() => setScope("apparatus")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${scope === "apparatus" ? "bg-gold text-[#020617]" : "text-muted-foreground hover:text-foreground"}`}>Per attrezzo</button>
          <button data-testid="ranking-scope-general" onClick={() => setScope("general")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${scope === "general" ? "bg-gold text-[#020617]" : "text-muted-foreground hover:text-foreground"}`}>Generale</button>
          <button data-testid="ranking-scope-groups" onClick={() => setScope("groups")} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${scope === "groups" ? "bg-gold text-[#020617]" : "text-muted-foreground hover:text-foreground"}`}>Gruppi</button>
        </div>
        <Button data-testid="open-liveboard-btn" onClick={() => window.open("/tabellone", "_blank")} variant="outline" className="border-white/15 hover:border-gold/60">
          <Monitor className="w-4 h-4 mr-1.5" /> Tabellone Live
        </Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList className="bg-[#0F172A] border border-white/10 h-auto p-1">
            <TabsTrigger data-testid="ranking-tab-artistica" value="artistica" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2">{discLabel.artistica}</TabsTrigger>
            <TabsTrigger data-testid="ranking-tab-ritmica" value="ritmica" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2">{discLabel.ritmica}</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger data-testid="ranking-event-select" className="w-[240px] bg-[#0F172A] border-white/15"><SelectValue placeholder="Tutte le gare" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ranking-event-all">Tutte le gare</SelectItem>
                {events.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`ranking-event-${e.id}`}>{e.title}{e.date ? ` — ${new Date(e.date).toLocaleDateString("it-IT")}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="ranking-export-pdf" onClick={() => exportPDF(tab)} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              <FileDown className="w-4 h-4 mr-1.5" /> Scarica Risultati in PDF
            </Button>
            {isManager && selectedEvent && (
              <Button data-testid="ranking-final-archive-btn" onClick={exportFinalArchive} disabled={archiving} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10">
                {archiving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} PDF Fine Gara
              </Button>
            )}
            {isManager && selectedEvent && (
              selectedEvent.locked ? (
                <Button data-testid="ranking-unlock-btn" onClick={() => toggleLock(false)} disabled={locking} variant="outline" className="border-white/15 hover:border-amber-400/60">
                  {locking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LockOpen className="w-4 h-4 mr-1.5" />} Riapri gara
                </Button>
              ) : (
                <Button data-testid="ranking-lock-btn" onClick={() => toggleLock(true)} disabled={locking} variant="outline" className="border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10">
                  {locking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Lock className="w-4 h-4 mr-1.5" />} Chiudi gara
                </Button>
              )
            )}
            {selectedEvent?.locked && selectedEvent?.snapshot?.url && (
              <Button data-testid="ranking-download-verbale" onClick={() => window.open(`${BACKEND}${selectedEvent.snapshot.url}`, "_blank")} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10">
                <FileText className="w-4 h-4 mr-1.5" /> Verbale ufficiale
              </Button>
            )}
          </div>
        </div>
        <TabsContent value="artistica" className="mt-6">{renderGroups("artistica")}</TabsContent>
        <TabsContent value="ritmica" className="mt-6">{renderGroups("ritmica")}</TabsContent>
      </Tabs>
    </div>
  );
}
